/**
 * Security Service
 * Provides rate limiting, brute force protection, and security utilities
 */

interface RateLimitEntry {
  count: number;
  resetTime: number;
  blocked: boolean;
  blockUntil?: number;
}

interface SecurityConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs: number;
  maxRequestsPerMinute: number;
}

// In-memory rate limit store (for production, use Redis or similar)
const rateLimitStore = new Map<string, RateLimitEntry>();

// Security configuration
const SECURITY_CONFIG: SecurityConfig = {
  maxAttempts: 5, // Max failed login attempts
  windowMs: 15 * 60 * 1000, // 15 minutes
  blockDurationMs: 30 * 60 * 1000, // Block for 30 minutes after max attempts
  maxRequestsPerMinute: 10, // Max requests per minute per IP/email
};

/**
 * Clean up expired rate limit entries
 */
const cleanupExpiredEntries = () => {
  const now = Date.now();
  for (const [key, entry] of rateLimitStore.entries()) {
    if (entry.resetTime < now && (!entry.blockUntil || entry.blockUntil < now)) {
      rateLimitStore.delete(key);
    }
  }
};

// Cleanup every 5 minutes
setInterval(cleanupExpiredEntries, 5 * 60 * 1000);

/**
 * Get client identifier (IP address or email)
 */
const getClientId = (identifier: string, type: 'ip' | 'email' = 'email'): string => {
  return `${type}:${identifier}`;
};

/**
 * Check if an action is rate limited
 */
export const checkRateLimit = (
  identifier: string,
  type: 'ip' | 'email' = 'email',
  action: 'login' | 'register' | 'password-reset' | 'api' = 'api'
): { allowed: boolean; retryAfter?: number; reason?: string } => {
  const now = Date.now();
  const key = `${action}:${getClientId(identifier, type)}`;
  const entry = rateLimitStore.get(key);

  // Check if blocked
  if (entry?.blocked && entry.blockUntil && entry.blockUntil > now) {
    const retryAfter = Math.ceil((entry.blockUntil - now) / 1000);
    return {
      allowed: false,
      retryAfter,
      reason: `Too many failed attempts. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`,
    };
  }

  // Check if within rate limit window
  if (entry && entry.resetTime > now) {
    // Check if exceeded max attempts
    if (entry.count >= SECURITY_CONFIG.maxAttempts) {
      const blockUntil = now + SECURITY_CONFIG.blockDurationMs;
      rateLimitStore.set(key, {
        ...entry,
        blocked: true,
        blockUntil,
      });
      const retryAfter = Math.ceil(SECURITY_CONFIG.blockDurationMs / 1000);
      return {
        allowed: false,
        retryAfter,
        reason: `Too many failed attempts. Account temporarily blocked. Please try again in ${Math.ceil(retryAfter / 60)} minutes.`,
      };
    }

    // Check if exceeded requests per minute
    if (action === 'api' && entry.count >= SECURITY_CONFIG.maxRequestsPerMinute) {
      const retryAfter = Math.ceil((entry.resetTime - now) / 1000);
      return {
        allowed: false,
        retryAfter,
        reason: 'Too many requests. Please slow down.',
      };
    }
  }

  return { allowed: true };
};

/**
 * Record a failed attempt
 */
export const recordFailedAttempt = (
  identifier: string,
  type: 'ip' | 'email' = 'email',
  action: 'login' | 'register' | 'password-reset' = 'login'
): void => {
  const now = Date.now();
  const key = `${action}:${getClientId(identifier, type)}`;
  const entry = rateLimitStore.get(key);

  if (entry && entry.resetTime > now) {
    // Within existing window, increment count
    rateLimitStore.set(key, {
      ...entry,
      count: entry.count + 1,
    });
  } else {
    // New window
    rateLimitStore.set(key, {
      count: 1,
      resetTime: now + SECURITY_CONFIG.windowMs,
      blocked: false,
    });
  }
};

/**
 * Record a successful attempt (reset counter)
 */
export const recordSuccess = (
  identifier: string,
  type: 'ip' | 'email' = 'email',
  action: 'login' | 'register' | 'password-reset' = 'login'
): void => {
  const key = `${action}:${getClientId(identifier, type)}`;
  rateLimitStore.delete(key);
};

/**
 * Record an API request
 */
export const recordApiRequest = (identifier: string, type: 'ip' | 'email' = 'ip'): void => {
  const now = Date.now();
  const key = `api:${getClientId(identifier, type)}`;
  const entry = rateLimitStore.get(key);

  if (entry && entry.resetTime > now) {
    rateLimitStore.set(key, {
      ...entry,
      count: entry.count + 1,
    });
  } else {
    rateLimitStore.set(key, {
      count: 1,
      resetTime: now + 60 * 1000, // 1 minute window
      blocked: false,
    });
  }
};

/**
 * Get client IP address (for server-side use)
 */
export const getClientIP = (): string => {
  // This is a placeholder - in production, get from request headers
  // For client-side, we'll use a combination of factors
  if (typeof window !== 'undefined') {
    // Use a combination of user agent and other factors as identifier
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Create a hash-like identifier (not perfect, but better than nothing)
    return btoa(`${userAgent}-${language}-${timezone}`).substring(0, 16);
  }
  return 'unknown';
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254; // RFC 5321 max length
};

/**
 * Validate password strength
 */
export const validatePasswordStrength = (password: string): {
  valid: boolean;
  score: number;
  feedback: string[];
} => {
  const feedback: string[] = [];
  let score = 0;

  if (password.length < 8) {
    feedback.push('Password must be at least 8 characters');
  } else {
    score += 1;
  }

  if (!/[a-z]/.test(password)) {
    feedback.push('Add lowercase letters');
  } else {
    score += 1;
  }

  if (!/[A-Z]/.test(password)) {
    feedback.push('Add uppercase letters');
  } else {
    score += 1;
  }

  if (!/[0-9]/.test(password)) {
    feedback.push('Add numbers');
  } else {
    score += 1;
  }

  if (!/[^a-zA-Z0-9]/.test(password)) {
    feedback.push('Add special characters');
  } else {
    score += 1;
  }

  return {
    valid: score >= 3 && password.length >= 8,
    score,
    feedback: feedback.length > 0 ? feedback : ['Strong password'],
  };
};

/**
 * Sanitize user input
 */
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 1000); // Limit length
};

/**
 * Check if request should be blocked (for DDoS protection)
 */
export const shouldBlockRequest = (identifier: string): boolean => {
  const rateLimit = checkRateLimit(identifier, 'ip', 'api');
  return !rateLimit.allowed;
};

/**
 * Security headers for responses (to be used in Cloud Functions or server)
 */
export const getSecurityHeaders = (): Record<string, string> => {
  return {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
    'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://*.googleapis.com https://*.gstatic.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https:; connect-src 'self' https://*.firebaseio.com https://*.googleapis.com wss://*.firebaseio.com;",
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
  };
};
